import React, { useEffect, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { PassportAlertsProvider } from "@/contexts/PassportAlertsContext";
import { UnreadChatProvider } from "@/contexts/UnreadChatContext";
import { ConfirmProvider } from "@/lib/confirm";
import ErrorBoundary from "@/components/ErrorBoundary";
import DeferredReloadManager from "@/components/DeferredReloadManager";
import MuteDelayedHandler from "@/components/MuteDelayedHandler";
import DashboardLayout from "@/components/layout/DashboardLayout";
import SourceDegradedBanner from "@/components/SourceDegradedBanner";
import lazyWithRetry from "@/lib/lazyWithRetry";

// Code-split por ruta — sólo carga el bundle de la página al navegar.
// Reduce JS inicial dramáticamente (Calendar/Maintenance pesan mucho).
// lazyWithRetry: si el chunk 404ea (deploy nuevo + cliente viejo), reintenta y
// recarga silenciosamente en vez de mostrar el ErrorBoundary.
const LoginPage = lazyWithRetry(() => import("@/pages/LoginPage"));
const DashboardPage = lazyWithRetry(() => import("@/pages/DashboardPage"));
const CalendarPage = lazyWithRetry(() => import("@/pages/CalendarPage"));
const FleetPage = lazyWithRetry(() => import("@/pages/fleet/FleetPage"));
const FlightsPage = lazyWithRetry(() => import("@/pages/FlightsPage"));
const TripsheetPage = lazyWithRetry(() => import("@/pages/tripsheet/TripsheetPage"));
const PostflightReportPage = lazyWithRetry(() => import("@/pages/postflight/PostflightReportPage"));
const MaintenancePage = lazyWithRetry(() => import("@/pages/MaintenancePage"));
const MaintenanceReportPage = lazyWithRetry(
  () => import("@/pages/maintenance/MaintenanceReportPage")
);
const StaffPage = lazyWithRetry(() => import("@/pages/StaffPage"));
const ExpensesPage = lazyWithRetry(() => import("@/pages/ExpensesPage"));
const LiveTrackingPage = lazyWithRetry(() => import("@/pages/LiveTrackingPage"));
const AIAgentPage = lazyWithRetry(() => import("@/pages/AIAgentPage"));
const CompaniesPage = lazyWithRetry(() => import("@/pages/companies/CompaniesPage"));
const AllUsersPage = lazyWithRetry(() => import("@/pages/AllUsersPage"));
const OwnersPage = lazyWithRetry(() => import("@/pages/OwnersPage"));
const SettingsPage = lazyWithRetry(() => import("@/pages/SettingsPage"));
const FuelAnalyticsPage = lazyWithRetry(() => import("@/pages/FuelAnalyticsPage"));
const FuelOrdersPage = lazyWithRetry(() => import("@/pages/FuelOrdersPage"));
const SuperAdminDashboard = lazyWithRetry(
  () => import("@/pages/superadmin-dashboard/SuperAdminDashboard")
);
const CompanyDetailPage = lazyWithRetry(() => import("@/pages/company-detail/CompanyDetailPage"));
const AlertsPage = lazyWithRetry(() => import("@/pages/AlertsPage"));
const AuditPage = lazyWithRetry(() => import("@/pages/AuditPage"));
const WeatherPage = lazyWithRetry(() => import("@/pages/weather/WeatherPage"));
const FlightCalculatorPage = lazyWithRetry(() => import("@/pages/FlightCalculatorPage"));
const CBPFactSheetsPage = lazyWithRetry(() => import("@/pages/CBPFactSheetsPage"));
const HoursOfOperationPage = lazyWithRetry(
  () => import("@/pages/hours-of-operation/HoursOfOperationPage")
);
const AdminAlertsPage = lazyWithRetry(() => import("@/pages/AdminAlertsPage"));
const SuperAdminAudit = lazyWithRetry(() => import("@/pages/SuperAdminAudit"));
const SuperAdminAipMx = lazyWithRetry(() => import("@/pages/SuperAdminAipMx"));
const SuperAdminFaaNasr = lazyWithRetry(() => import("@/pages/SuperAdminFaaNasr"));
const SuperAdminWebVitals = lazyWithRetry(() => import("@/pages/SuperAdminWebVitals"));
const SuperAdminETM = lazyWithRetry(() => import("@/pages/SuperAdminETM"));
const FuelPriceSimulator = lazyWithRetry(() => import("@/pages/FuelPriceSimulator"));
const HoursLogPage = lazyWithRetry(() => import("@/pages/hours-log/HoursLogPage"));
const ScheduledInspectionsPage = lazyWithRetry(() => import("@/pages/ScheduledInspectionsPage"));
const RequestTripPage = lazyWithRetry(() => import("@/pages/RequestTripPage"));
const ETMPage = lazyWithRetry(() => import("@/pages/ETMPage"));
const MessagesPage = lazyWithRetry(() => import("@/pages/MessagesPage"));
const OnboardingWizardPage = lazyWithRetry(() => import("@/pages/onboarding/OnboardingWizardPage"));

function AltivSplash() {
  return <div className="min-h-screen bg-[#0f172a]" />;
}

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-6 h-6 border-2 border-slate-700 border-t-[#0052FF] rounded-full animate-spin" />
    </div>
  );
}

function SplashController() {
  const { loading } = useAuth();
  useEffect(() => {
    if (loading) return;
    const splash = document.getElementById("altiv-splash");
    if (!splash) {
      document.documentElement.classList.remove("altiv-splash-active");
      return;
    }
    splash.classList.add("altiv-splash-hidden");
    const t = setTimeout(() => {
      splash.remove();
      document.documentElement.classList.remove("altiv-splash-active");
    }, 250);
    return () => clearTimeout(t);
  }, [loading]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AltivSplash />;
  return user ? children : <Navigate to="/" replace />;
}

function DashboardRoute() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/superadmin" replace />;
  if (user?.role === "ground_support" || user?.role === "mechanic")
    return <Navigate to="/calendar" replace />;
  if (user?.role === "owner") return <Navigate to="/calendar" replace />;
  if (user?.role === "accountant") return <Navigate to="/expenses" replace />;
  return <DashboardPage />;
}

// Accountant is a finance-only role: its sidebar is just Gastos. Without a route
// guard, a deep-link (typed URL, stale bookmark, search result) rendered the full
// operational screens (Fleet, Live Tracking, Staff, …) — defense-in-depth gap
// (F4, audit 2026-06-04). Backend scoping still applies; this stops the UI from
// surfacing off-scope screens. Allow-list keeps profile/settings + the
// accountant's own notifications/chat reachable; everything else → Gastos.
const ACCOUNTANT_ALLOWED_PREFIXES = ["/expenses", "/settings", "/alerts", "/messages"];

function RoleScopeGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.role === "accountant") {
    const path = location.pathname;
    const allowed = ACCOUNTANT_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
    if (!allowed) return <Navigate to="/expenses" replace />;
  }
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AltivSplash />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AltivSplash />;
  // Setup deep-link del welcome email (`/?setup=1&email=…&code=…`): el LoginPage
  // necesita renderizar para meter al usuario en el flow de set password, aunque
  // ya haya OTRA sesión activa en el browser (típico cuando el super_admin abre
  // su propio link en la misma pestaña). Si redirigimos por rol, nunca llega
  // al setup. Detectamos el query param antes y dejamos pasar.
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("setup") === "1" && params.get("email") && params.get("code")) {
      return children;
    }
  }
  if (user) {
    if (user.role === "super_admin") return <Navigate to="/superadmin" replace />;
    if (user.role === "ground_support" || user.role === "mechanic")
      return <Navigate to="/calendar" replace />;
    if (user.role === "owner") return <Navigate to="/calendar" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <SplashController />
            <BrowserRouter>
              <DeferredReloadManager />
              <MuteDelayedHandler />
              {/* External-source health banner — sits above all routes
                  so a SENEAM/FAA outage is visible even on the login
                  page, where the operator might not yet realize that
                  airport hours are stale. Self-hides when all sources
                  recover; dismiss is per-tab session. */}
              <SourceDegradedBanner />
              <Toaster
                position="top-center"
                theme="dark"
                richColors
                closeButton
                toastOptions={{ duration: 5000 }}
              />
              <ConfirmProvider>
                <PassportAlertsProvider>
                  <UnreadChatProvider>
                    <Suspense fallback={<PageFallback />}>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <PublicRoute>
                              <LoginPage />
                            </PublicRoute>
                          }
                        />
                        {/* Public onboarding wizard — capability link, no auth.
                            The token in the URL is the credential. */}
                        <Route path="/onboarding/:token" element={<OnboardingWizardPage />} />
                        {/* All authenticated routes share DashboardLayout (sidebar) */}
                        <Route
                          element={
                            <ProtectedRoute>
                              <RoleScopeGuard>
                                <DashboardLayout />
                              </RoleScopeGuard>
                            </ProtectedRoute>
                          }
                        >
                          <Route path="/dashboard" element={<DashboardRoute />} />
                          <Route path="/calendar" element={<CalendarPage />} />
                          <Route path="/fleet" element={<FleetPage />} />
                          <Route path="/flights" element={<FlightsPage />} />
                          <Route path="/trips/:tripId/tripsheet" element={<TripsheetPage />} />
                          <Route path="/postflight/:tripId" element={<PostflightReportPage />} />
                          <Route path="/maintenance" element={<MaintenancePage />} />
                          {/* `/etm` ANTES del comodín `:reportId`, o el ETM se
                              leería como el id de un reporte y saldría 404. */}
                          <Route path="/maintenance/etm" element={<ETMPage />} />
                          <Route
                            path="/maintenance/:reportId"
                            element={<MaintenanceReportPage />}
                          />
                          <Route path="/staff" element={<StaffPage />} />
                          <Route path="/owners" element={<OwnersPage />} />
                          <Route path="/expenses" element={<ExpensesPage />} />
                          <Route path="/tracking" element={<LiveTrackingPage />} />
                          <Route path="/ai" element={<AIAgentPage />} />
                          <Route path="/companies" element={<CompaniesPage />} />
                          <Route path="/all-users" element={<AllUsersPage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="/fuel-analytics" element={<FuelAnalyticsPage />} />
                          <Route path="/fuel-orders" element={<FuelOrdersPage />} />
                          <Route path="/alerts" element={<AlertsPage />} />
                          <Route path="/audit" element={<AuditPage />} />
                          <Route path="/weather" element={<WeatherPage />} />
                          <Route path="/flight-calculator" element={<FlightCalculatorPage />} />
                          <Route path="/cbp" element={<CBPFactSheetsPage />} />
                          <Route path="/hours-of-operation" element={<HoursOfOperationPage />} />
                          <Route path="/hours-log" element={<HoursLogPage />} />
                          <Route
                            path="/scheduled-inspections"
                            element={<ScheduledInspectionsPage />}
                          />
                          <Route path="/request-trip" element={<RequestTripPage />} />
                          <Route path="/messages" element={<MessagesPage />} />
                          {/* SuperAdmin routes — inside layout so sidebar is present */}
                          <Route
                            path="/superadmin"
                            element={
                              <SuperAdminRoute>
                                <SuperAdminDashboard />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/companies/:companyId"
                            element={
                              <SuperAdminRoute>
                                <CompanyDetailPage />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/admin/alerts"
                            element={
                              <SuperAdminRoute>
                                <AdminAlertsPage />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/flight-events"
                            element={
                              <SuperAdminRoute>
                                <SuperAdminAudit />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/fuel-price-simulator"
                            element={
                              <SuperAdminRoute>
                                <FuelPriceSimulator />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/aip-mx"
                            element={
                              <SuperAdminRoute>
                                <SuperAdminAipMx />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/faa-nasr"
                            element={
                              <SuperAdminRoute>
                                <SuperAdminFaaNasr />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/etm"
                            element={
                              <SuperAdminRoute>
                                <SuperAdminETM />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/superadmin/web-vitals"
                            element={
                              <SuperAdminRoute>
                                <SuperAdminWebVitals />
                              </SuperAdminRoute>
                            }
                          />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Suspense>
                  </UnreadChatProvider>
                </PassportAlertsProvider>
              </ConfirmProvider>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
